/**
 * Shared texting for a company's admins (1.12.32): Texts go to for a number (Numbers, a person's
 * page), a ring group's or queue's Texting rules (Ring groups, Call queues), and a person's numbers
 * for texts. Everything here is kept in Stitch: nothing changes on the phone system.
 */
import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { api, type TeamKind, type TeamRules, type TeamRulesPage, type TextRouteKind, type TextRouteRow, type TextRoutesPage } from '../api/client';
import { Button, Card, Chip, Dialog, Drawer, Empty, LoadError, Loading, Notice, TextAreaField } from './ui';
import { useAuth } from '../lib/auth';
import { shortWhen } from '../lib/format';
import { Icon } from '../lib/icons';
import { useData } from '../lib/useData';
import {
  RULE_NUMBERS,
  TEAM_WORD,
  fillAutoText,
  personNumbers,
  plainRulesError,
  ruleInputs,
  rulesToSave,
  sameAsCalls,
  teamLabel,
  textsGoTo,
  type RuleNumber
} from '../lib/textRouting';

/** The company's own admins (not Stitch staff looking in): only they see and change where texts go. */
export function useCompanyAdmin(domain: string): boolean {
  const { state } = useAuth();
  return state?.memberships?.some((m) => m.tenant === domain.toLowerCase() && m.role === 'admin') ?? false;
}

const errorText = (err: unknown) => (err instanceof Error ? err.message : 'That didn’t save. Try again.');

/** An on/off switch with its words beside it (the words turn it on and off too). */
function SwitchRow({ on, set, label, hint, disabled }: { on: boolean; set: (on: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className={`tt-switch${disabled ? ' disabled' : ''}`}>
      <span className="cell-stack grow">
        <label htmlFor={id} className="tt-switch-label">
          {label}
        </label>
        {hint && (
          <small className="muted" id={`${id}-hint`}>
            {hint}
          </small>
        )}
      </span>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={on}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={`toggle${on ? ' on' : ''}`}
        disabled={disabled}
        onClick={() => set(!on)}
      >
        <span />
      </button>
    </div>
  );
}

// --- Texts go to (Numbers, 2959) -------------------------------------------------------------------

const KIND_ORDER: { kind: TextRouteKind; title: string; sub: string }[] = [
  { kind: 'same', title: 'Same as calls', sub: '' },
  { kind: 'person', title: 'A person', sub: 'Their own texts, like a second number of theirs.' },
  { kind: 'ringGroup', title: 'A ring group', sub: 'Its people share them, by its texting rules.' },
  { kind: 'callQueue', title: 'A call queue', sub: 'Its agents share them, by its texting rules.' },
  { kind: 'none', title: 'Nobody (texting off)', sub: 'Stitch doesn’t send texts from it. Any that still come in go to your admins.' }
];

/**
 * Where one number's texts go: the same as its calls (the standard), a person, a ring group, a
 * queue, or nobody. Only texts change; calls still follow the phone system.
 */
export function TextsGoToDrawer({
  domain,
  row,
  page,
  name,
  onClose,
  onSaved
}: {
  domain: string;
  row: TextRouteRow;
  page: TextRoutesPage;
  /** The number's name on the phone system ("Main line"), when it has one. */
  name?: string;
  onClose: () => void;
  onSaved: (next: TextRoutesPage, message: string) => void;
}) {
  const label = formatPhoneNumber(row.number);
  const now = row.texts.set ? row.texts.kind : 'same';
  const [kind, setKind] = useState<TextRouteKind>(now);
  const teamsOf = (k: TeamKind) => page.teams.filter((t) => t.kind === k);
  const people = [...page.people].sort((a, b) => a.name.localeCompare(b.name));
  const first = (k: TextRouteKind) =>
    k === 'person' ? (people[0]?.extension ?? '') : k === 'ringGroup' || k === 'callQueue' ? (teamsOf(k)[0]?.extension ?? '') : '';
  const [targets, setTargets] = useState<Record<string, string>>(() => ({
    person: now === 'person' ? row.texts.target : first('person'),
    ringGroup: now === 'ringGroup' ? row.texts.target : first('ringGroup'),
    callQueue: now === 'callQueue' ? row.texts.target : first('callQueue')
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const target = targets[kind] ?? '';
  const needsTarget = kind === 'person' || kind === 'ringGroup' || kind === 'callQueue';
  const unchanged = kind === now && (!needsTarget || target === row.texts.target);
  const chosenTeam = kind === 'ringGroup' || kind === 'callQueue' ? page.teams.find((t) => t.extension === target) : undefined;
  // A company without ring groups (or queues) isn't offered one, unless the number's texts go to one now.
  const shown = (k: TextRouteKind) => (k !== 'ringGroup' && k !== 'callQueue') || teamsOf(k).length > 0 || now === k;

  async function save() {
    setBusy(true);
    setError('');
    try {
      const next = await api.setTextRoute(domain, row.number, kind, needsTarget ? target : undefined);
      const after = next.numbers.find((n) => n.number === row.number);
      onSaved(next, after ? `Texts to ${label}: ${textsGoTo(after, next.teams).text}.` : `Saved where texts to ${label} go.`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Texts to ${label}`}
      subtitle={`${name && name !== row.number && name !== label ? `${name}. ` : ''}Calls go to ${row.calls.kind === 'none' ? 'nowhere yet' : teamLabel(row.calls.kind, row.calls.name)}.`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || unchanged || (needsTarget && !target)} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <fieldset className="plain-fieldset tt-choices">
        <legend className="tt-legend">Send its texts to</legend>
        {KIND_ORDER.filter((o) => shown(o.kind)).map((o) => {
          const on = kind === o.kind;
          const none = o.kind === 'person' ? people.length === 0 : o.kind === 'ringGroup' || o.kind === 'callQueue' ? teamsOf(o.kind).length === 0 : false;
          return (
            <label key={o.kind} className={`radio-card tt-choice${none ? ' disabled' : ''}`}>
              <input type="radio" name="texts-go-to" checked={on} disabled={none} onChange={() => setKind(o.kind)} />
              <span className="cell-stack grow">
                <strong>{o.title}</strong>
                <small className="muted">{o.kind === 'same' ? sameAsCalls(row, page) : none && o.kind === 'person' ? 'Nobody at your company uses Stitch yet.' : o.sub}</small>
                {on && o.kind === 'person' && (
                  <select className="input tt-pick" aria-label="Which person" value={target} onChange={(e) => setTargets((t) => ({ ...t, person: e.target.value }))}>
                    {people.map((p) => (
                      <option key={p.extension} value={p.extension}>
                        {p.name} ({p.extension})
                      </option>
                    ))}
                  </select>
                )}
                {on && (o.kind === 'ringGroup' || o.kind === 'callQueue') && (
                  <select
                    className="input tt-pick"
                    aria-label={o.kind === 'ringGroup' ? 'Which ring group' : 'Which call queue'}
                    value={target}
                    onChange={(e) => setTargets((t) => ({ ...t, [o.kind]: e.target.value }))}
                  >
                    {teamsOf(o.kind).map((t) => (
                      <option key={t.extension} value={t.extension}>
                        {t.name} · {t.takesTexts ? (t.people === 1 ? '1 person in Stitch' : `${t.people} people in Stitch`) : 'texts off'}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            </label>
          );
        })}
      </fieldset>
      {chosenTeam && !chosenTeam.takesTexts && (
        <Notice tone="warn">
          {chosenTeam.name} doesn’t take texts now, so its texts would go to your admins. On {chosenTeam.kind === 'callQueue' ? 'Call Queues' : 'Ring Groups'}, choose Texts off on
          its row and switch it on.
        </Notice>
      )}
      {chosenTeam && chosenTeam.takesTexts && chosenTeam.people === 0 && (
        <Notice tone="warn">Nobody in {chosenTeam.name} uses Stitch yet, so its texts would go to its supervisors, or to your admins if it has none.</Notice>
      )}
      <p className="tt-only-texts">
        <Icon name="info" size={16} /> Only texts change. Calls still go where the phone system sends them.
      </p>
      <p className="muted small tt-note">
        Its earlier conversations go with it: whoever gets its texts from now on sees them. The change is written to Activity.
        {row.changedBy && row.changedAt ? ` Last changed by ${row.changedBy}, ${shortWhen(row.changedAt)}.` : ''}
      </p>
      {error && <Notice tone="crit">{error}</Notice>}
    </Drawer>
  );
}

// --- A ring group's or queue's texting rules (2960) ----------------------------------------------

/** The Texting rules panel for one team: loads its rules, then the form. */
export function TeamTextingDrawer({ domain, team, onClose, onSaved }: { domain: string; team: { extension: string; name: string; kind: TeamKind }; onClose: () => void; onSaved: (page: TeamRulesPage) => void }) {
  const page = useData(() => api.teamRules(domain, team.extension), [domain, team.extension], { live: false });
  if (!page.data) {
    return (
      <Drawer open onClose={onClose} title={`${team.name} texting rules`} subtitle={`${TEAM_WORD[team.kind][0]!.toUpperCase()}${TEAM_WORD[team.kind].slice(1)} ${team.extension}`}>
        {page.error ? <LoadError error={page.error} retry={page.reload} /> : <Loading label="Loading its texting rules" />}
      </Drawer>
    );
  }
  return <TeamRulesForm domain={domain} page={page.data} onClose={onClose} onSaved={onSaved} />;
}

type TextKey = 'place.text' | 'afterHours.text' | 'opening.text';

function TeamRulesForm({ domain, page, onClose, onSaved }: { domain: string; page: TeamRulesPage; onClose: () => void; onSaved: (page: TeamRulesPage) => void }) {
  const t = page.team;
  const queue = t.kind === 'callQueue';
  const [rules, setRules] = useState<TeamRules>(page.rules);
  const [nums, setNums] = useState<Record<RuleNumber, string>>(() => ruleInputs(page.rules));
  // After a Save that needed a look, the settings are checked as they're typed (a mark goes once fixed).
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = <K extends keyof TeamRules>(k: K, v: TeamRules[K]) => setRules((r) => ({ ...r, [k]: v }));
  const num = (k: RuleNumber) => (v: string) => setNums((n) => ({ ...n, [k]: v }));

  const built = rulesToSave(rules, nums, page.maxText);
  const errors: Partial<Record<RuleNumber | TextKey, string>> = checked ? built.errors : {};
  const needsLook = checked && !built.rules;
  const changed = !built.rules || JSON.stringify(built.rules) !== JSON.stringify(page.rules);
  const atDefaults = JSON.stringify(rules) === JSON.stringify(page.defaults) && JSON.stringify(nums) === JSON.stringify(ruleInputs(page.defaults));
  const vars = { company: page.company, team: t.name, place: 2, wait: 'about 6 minutes' };
  const people = useMemo(() => [...page.people].sort((a, b) => a.name.localeCompare(b.name)), [page.people]);
  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? 'Someone no longer here';
  const numbers = page.numbers.map((n) => n.label);
  const members = page.members.map((m) => m.name);
  const teamRuleName = page.teamRule?.name;
  const form = useRef<HTMLDivElement>(null);
  /** The panel scrolls: what needs a look is brought into view (the first setting, or the message). */
  const show = (selector: string) =>
    requestAnimationFrame(() => {
      const el = form.current?.querySelector<HTMLElement>(selector);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (el?.matches('input, textarea')) el.focus({ preventScroll: true });
    });

  async function save() {
    const check = rulesToSave(rules, nums, page.maxText);
    setChecked(true);
    setError('');
    if (!check.rules) {
      show('[aria-invalid="true"]');
      return;
    }
    setBusy(true);
    try {
      onSaved(await api.setTeamRules(domain, t.extension, check.rules));
    } catch (err) {
      setError(plainRulesError(err));
      setBusy(false);
      show('.tt-save-error');
    }
  }

  function backToDefaults() {
    setRules(page.defaults);
    setNums(ruleInputs(page.defaults));
  }

  const numberInput = (k: RuleNumber, label: string) => (
    <input
      type="number"
      inputMode="numeric"
      className="input tt-num"
      min={RULE_NUMBERS[k].min}
      max={RULE_NUMBERS[k].max}
      value={nums[k]}
      aria-label={label}
      aria-invalid={errors[k] ? true : undefined}
      onChange={(e) => num(k)(e.target.value)}
    />
  );
  const fieldError = (k: RuleNumber | TextKey) =>
    errors[k] ? (
      <span className="tt-error" role="alert">
        {errors[k]}
      </span>
    ) : null;

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${t.name} texting rules`}
      subtitle={`${queue ? 'Call queue' : 'Ring group'} ${t.extension} · ${members.length === 1 ? `1 ${queue ? 'agent' : 'person'}` : `${members.length} ${queue ? 'agents' : 'people'}`} in Stitch`}
      foot={
        <>
          <button type="button" className="link-btn left" disabled={atDefaults} onClick={backToDefaults}>
            Back to defaults
          </button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !changed} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="team-texting" ref={form}>
        <section className={`tt-takes${rules.takesTexts ? ' on' : ''}`} aria-label="Takes texts">
          <SwitchRow
            on={rules.takesTexts}
            set={(v) => set('takesTexts', v)}
            label={<strong>{t.name} takes texts</strong>}
            hint={
              rules.takesTexts
                ? `Texts to its numbers come to its ${queue ? 'agents' : 'people'}, by the rules below, and the wallboard shows ${t.name}’s texts.`
                : 'Its numbers’ texts go to your admins, and the wallboard shows no texts for it. The rules below rest until it’s on again.'
            }
          />
          {numbers.length ? (
            <p className="tt-numbers">
              <Icon name="hash" size={15} /> Texts to {numbers.join(', ')} {rules.takesTexts ? 'come here.' : 'come here when it’s on.'}
            </p>
          ) : (
            <p className="tt-numbers muted">
              No numbers send texts here yet. Choose <strong>Texts go to</strong> on the <Link to={`/t/${encodeURIComponent(domain)}/numbers`}>Numbers</Link> page.
            </p>
          )}
          {!members.length && (
            <Notice tone="warn">
              Nobody in {t.name} uses Stitch yet, so its texts go to {rules.supervisors.length ? 'its supervisors only' : 'your admins'}. Invite its people on Portal access.
            </Notice>
          )}
        </section>

        <fieldset className="tt-rest" disabled={!rules.takesTexts}>
          <legend className="sr-only">{t.name}’s rules</legend>
          <section className="tt-section">
            <h3>Handing out new texts</h3>
            <div className="radio-cards compact">
              <label className="radio-card">
                <input type="radio" name="tt-handout" checked={rules.handout === 'everyone'} onChange={() => set('handout', 'everyone')} />
                <span className="cell-stack">
                  <strong>To everyone</strong>
                  <small className="muted">Everyone available is told. The first to reply, or to choose Take it, has it.</small>
                </span>
              </label>
              <label className="radio-card">
                <input type="radio" name="tt-handout" checked={rules.handout === 'one'} onChange={() => set('handout', 'one')} />
                <span className="cell-stack">
                  <strong>One at a time</strong>
                  <small className="muted">Stitch gives it to the available {queue ? 'agent' : 'person'} with the fewest open conversations.</small>
                </span>
              </label>
            </div>
            <div className="tt-line">
              At most {numberInput('limit', 'Open conversations per person')} open conversations per person
            </div>
            {fieldError('limit')}
            <fieldset className="plain-fieldset tt-sub">
              <legend>{queue ? 'Agents' : 'People'} on a call</legend>
              <label className="check">
                <input type="radio" name="tt-oncall" checked={rules.onCall === 'last'} onChange={() => set('onCall', 'last')} />
                Pick them last
              </label>
              <label className="check">
                <input type="radio" name="tt-oncall" checked={rules.onCall === 'skip'} onChange={() => set('onCall', 'skip')} />
                Skip them until the call ends (calls come first)
              </label>
            </fieldset>
            <div className="tt-line">
              Send returning customers back to whoever helped them last, within {numberInput('returningDays', 'Days to send returning customers back')} days, if they’re
              available
            </div>
            <small className="muted tt-after">0: hand them out like new ones.</small>
            {fieldError('returningDays')}
          </section>

          <section className="tt-section">
            <h3>Who’s available</h3>
            <p className="muted">
              {queue
                ? 'Agents signed in to the queue who have Stitch open, aren’t on do not disturb, invisible or offline, and have fewer open conversations than the limit. Signed out, they can read its texts but not answer them.'
                : 'People in the ring group who have Stitch open, aren’t on do not disturb, invisible or offline, and have fewer open conversations than the limit.'}
            </p>
            <p className="tt-members">
              {members.length ? (
                <>
                  <strong>In Stitch:</strong> {members.join(', ')}
                </>
              ) : (
                <span className="muted">Nobody in {t.name} uses Stitch yet.</span>
              )}
            </p>
          </section>

          <section className="tt-section">
            <h3>Timers</h3>
            <p className="muted">They count only while {t.name} is open.</p>
            <div className="tt-line">
              Reply within {numberInput('replyMinutes', 'Minutes to reply')} minutes, then {rules.handout === 'one' ? 'give it to the next person' : 'tell everyone again'}
            </div>
            {fieldError('replyMinutes')}
            <div className="tt-line">
              Tell the supervisors after {numberInput('supervisorMinutes', 'Minutes before supervisors are told')} minutes with no reply
            </div>
            {queue && <small className="muted tt-after">With nobody signed in to the queue, they’re told straight away.</small>}
            {fieldError('supervisorMinutes')}
            <div className="tt-line">
              When the person who has it goes away (do not disturb or offline), put the texts waiting for their reply back in line after{' '}
              {numberInput('awayMinutes', 'Minutes away')} minutes
            </div>
            {fieldError('awayMinutes')}
            {queue && (
              <>
                <div className="tt-line">
                  When an agent signs out of the queue, put their conversations back after {numberInput('signedOutMinutes', 'Minutes signed out')} minutes
                </div>
                {fieldError('signedOutMinutes')}
              </>
            )}
          </section>

          <section className="tt-section">
            <h3>Open hours</h3>
            <div className="field">
              <label htmlFor="tt-hours">{t.name} is open</label>
              <select id="tt-hours" className="input" value={rules.hours} onChange={(e) => set('hours', e.target.value)}>
                <option value="team">
                  {!teamRuleName
                    ? 'By its own after-hours rule (none yet, so always open)'
                    : page.teamRule?.usual
                      ? `By your company’s usual hours: ${teamRuleName}`
                      : `By its own after-hours rule: ${teamRuleName}`}
                </option>
                <option value="always">Always open</option>
                {page.timeRules
                  .filter((r) => r.extension !== page.teamRule?.extension || rules.hours === r.extension)
                  .map((r) => (
                    <option key={r.extension} value={r.extension}>
                      By another time of day rule: {r.name}
                    </option>
                  ))}
                {rules.hours !== 'team' && rules.hours !== 'always' && !page.timeRules.some((r) => r.extension === rules.hours) && <option value={rules.hours}>A rule that isn’t here now ({rules.hours})</option>}
              </select>
              <span className="hint">
                {rules.hours === page.rules.hours ? (
                  <>
                    <Chip small tone={page.open ? 'ok' : 'neutral'}>
                      {page.open ? 'Open now' : 'Closed now'}
                    </Chip>{' '}
                  </>
                ) : null}
                Closed, new texts wait and are handed out when it opens.
                {rules.hours === 'team' && page.teamRule?.usual && ' Call queues have no after-hours rule of their own, so this one follows your company’s usual hours: the rule most of your ring groups use, or your only one.'}
              </span>
            </div>
          </section>

          <section className="tt-section">
            <h3>While they wait</h3>
            <SwitchRow
              on={rules.place.on}
              set={(v) => set('place', { ...rules.place, on: v })}
              label="Tell them their place in line"
              hint="Like a queue’s place-in-line announcements on calls. Never sent once someone has answered."
            />
            <div className={`tt-line${rules.place.on ? '' : ' off'}`}>
              Send it if nobody has answered within {numberInput('place.firstMinutes', 'Minutes before the first place-in-line text')} minutes, and one update after{' '}
              {numberInput('place.updateMinutes', 'Minutes before the update')} minutes
            </div>
            {fieldError('place.firstMinutes')}
            {fieldError('place.updateMinutes')}
            <AutoText
              label="The text"
              value={rules.place.text}
              on={rules.place.on}
              max={page.maxText}
              error={errors['place.text']}
              preview={fillAutoText(rules.place.text, vars)}
              onChange={(text) => set('place', { ...rules.place, text })}
            />
            <div className="tt-blanks">
              <span className="muted">Blanks Stitch fills in, in any of these texts:</span>
              <ul>
                <li>
                  <code>{'{company}'}</code> {page.company}
                </li>
                <li>
                  <code>{'{team}'}</code> {t.name}
                </li>
                <li>
                  <code>{'{place}'}</code> their place in line (2 in the example)
                </li>
                <li>
                  <code>{'{wait}'}</code> the usual wait, from how fast {t.name} answered lately (“about 6 minutes”)
                </li>
              </ul>
            </div>
          </section>

          <section className="tt-section">
            <h3>After hours</h3>
            <SwitchRow
              on={rules.afterHours.on}
              set={(v) => set('afterHours', { ...rules.afterHours, on: v })}
              label="Reply while it’s closed"
              hint={`Once per conversation while ${t.name} is closed.`}
            />
            <AutoText
              label="The reply"
              value={rules.afterHours.text}
              on={rules.afterHours.on}
              max={page.maxText}
              error={errors['afterHours.text']}
              preview={fillAutoText(rules.afterHours.text, vars)}
              onChange={(text) => set('afterHours', { ...rules.afterHours, text })}
            />
            <SwitchRow
              on={rules.opening.on}
              set={(v) => set('opening', { ...rules.opening, on: v })}
              label="Opening text"
              hint="When it opens, everyone who texted while it was closed gets one text, if nobody has answered yet."
            />
            <AutoText
              label="The opening text"
              value={rules.opening.text}
              on={rules.opening.on}
              max={page.maxText}
              error={errors['opening.text']}
              preview={fillAutoText(rules.opening.text, vars)}
              onChange={(text) => set('opening', { ...rules.opening, text })}
            />
            <Notice>
              No automatic texts go out between 9 PM and 8 AM where the customer is, or to anyone who texted STOP. The first one in a conversation ends with “Reply STOP to opt
              out.” Each is billed like any text.
            </Notice>
          </section>

          <section className="tt-section">
            <h3>Owning a conversation</h3>
            <SwitchRow
              on={rules.ownerOnly}
              set={(v) => set('ownerOnly', v)}
              label="Only the person who has it replies"
              hint="Others see who has it, and choose Take over to reply. That person is told, and it’s noted in Activity."
            />
          </section>

          <section className="tt-section">
            <h3>Supervisors</h3>
            <p className="muted">They see every {t.name} text, hand conversations to someone, and are told when a text waits too long.</p>
            <Supervisors chosen={rules.supervisors} people={people} nameOf={nameOf} onChange={(v) => set('supervisors', v)} />
          </section>

          <section className="tt-section">
            <h3>
              <Icon name="spark" size={16} /> Stitch AI
            </h3>
            <SwitchRow
              on={rules.summaries}
              set={(v) => set('summaries', v)}
              label="Summary beside each conversation"
              hint="What’s going on, what’s still open, earlier conversations with the same number, and a reply to use or ignore. Needs Smart messaging and Catch me up (AI) switched on in Settings → Messages."
            />
          </section>

          <section className="tt-section">
            <h3>Urgent texts</h3>
            <SwitchRow
              on={rules.urgent}
              set={(v) => set('urgent', v)}
              label="Urgent words jump the line"
              hint="Texts that say urgent, emergency, asap, right away, immediately or 911 go first, and everyone available is told."
            />
          </section>
        </fieldset>
        {(error || needsLook) && (
          <div className="tt-save-error">
            <Notice tone="crit">{error || 'Some settings need a look: they’re marked in red.'}</Notice>
          </div>
        )}
      </div>
    </Drawer>
  );
}

/** An automatic text: what it says, filled in as it would go out, and its length. */
function AutoText({ label, value, on, max, error, preview, onChange }: { label: string; value: string; on: boolean; max: number; error?: string; preview: string; onChange: (v: string) => void }) {
  const over = value.trim().length > max;
  return (
    <div className={`tt-auto${on ? '' : ' off'}`}>
      <TextAreaField label={label} rows={3} value={value} disabled={!on} error={error} onChange={(e) => onChange(e.target.value)} />
      <div className="tt-auto-foot">
        <span className={`tt-count${over ? ' over' : ''}`}>
          {value.trim().length} / {max} characters
        </span>
      </div>
      {on && preview && (
        <div className="tt-preview" aria-label={`${label}, as it would go out`}>
          <span className="tt-preview-label">As it goes out</span>
          <p className="tt-bubble">{preview}</p>
        </div>
      )}
    </div>
  );
}

function Supervisors({ chosen, people, nameOf, onChange }: { chosen: string[]; people: { id: string; name: string }[]; nameOf: (id: string) => string; onChange: (v: string[]) => void }) {
  const left = people.filter((p) => !chosen.includes(p.id));
  return (
    <div className="tt-supervisors">
      {chosen.length > 0 ? (
        <ul className="tt-chips" aria-label="Supervisors">
          {chosen.map((id) => (
            <li key={id} className="tt-person-chip">
              {nameOf(id)}
              <button type="button" aria-label={`Remove ${nameOf(id)}`} onClick={() => onChange(chosen.filter((x) => x !== id))}>
                <Icon name="x" size={13} strokeWidth={2.4} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="muted">No supervisors yet.</span>
      )}
      {left.length > 0 && (
        <select className="input tt-add" aria-label="Add a supervisor" value="" onChange={(e) => e.target.value && onChange([...chosen, e.target.value])}>
          <option value="">Add a supervisor…</option>
          {left.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// --- A person's numbers for texts (their page) ------------------------------------------------------

/**
 * The numbers whose texts come to someone: their own (calls and texts, or texts only), and their
 * teams' numbers. Admins give them another number here (its texts go to them; calls don't change).
 */
export function PersonTextsCard({
  domain,
  extension,
  name,
  teams,
  base
}: {
  domain: string;
  extension: string;
  name: string;
  /** The ring groups and queues they're in (from where calls ring them). */
  teams: { extension: string; name: string; kind: string; where: string }[];
  base: string;
}) {
  const routes = useData(() => api.textRoutes(domain), [domain]);
  const textTeams = useData(() => api.textTeams(domain).catch(() => null), [domain]);
  const [giving, setGiving] = useState(false);
  const [changing, setChanging] = useState<string | null>(null);
  const [saved, setSaved] = useState('');
  const page = routes.data;
  const inStitch = Boolean(page?.people.some((p) => p.extension === extension));
  const own = page ? personNumbers(page.numbers, extension) : [];
  const shared = (textTeams.data?.teams ?? [])
    .map((tt) => ({ tt, ref: teams.find((r) => r.extension === tt.extension) }))
    .filter((x): x is { tt: (typeof x)['tt']; ref: NonNullable<(typeof x)['ref']> } => Boolean(x.ref && x.tt.takesTexts && x.tt.numbers.length))
    .flatMap(({ tt, ref }) => tt.numbers.map((n) => ({ number: n, team: tt, where: ref.where })));
  const free = page ? page.numbers.filter((r) => r.canText && !own.some((o) => o.row.number === r.number)) : [];
  const row = changing ? page?.numbers.find((n) => n.number === changing) : undefined;

  const reload = (message: string) => {
    setSaved(message);
    routes.reload();
    textTeams.reload();
  };

  const body = routes.error ? (
    <LoadError error={routes.error} retry={routes.reload} />
  ) : !page ? (
    <Loading label="Loading their numbers" />
  ) : (
    <div className="card-body section-stack person-texts">
      {!inStitch ? (
        <span className="muted">Texts come only to people who use Stitch. Invite {name} (Stitch sign-in) to give them a number for texts.</span>
      ) : own.length === 0 ? (
        <span className="muted">No number’s texts come to {name} yet.</span>
      ) : (
        <ul className="plain-list person-text-list" aria-label={`${name}’s numbers for texts`}>
          {own.map(({ row: r, use }) => (
            <li key={r.number}>
              <Icon name="chat" size={16} />
              <strong className="mono">{r.label}</strong>
              <Chip small tone={use === 'Texts only' ? 'brand' : 'neutral'}>
                {use}
              </Chip>
              <button type="button" className="link-btn small push" aria-label={`Change where texts to ${r.label} go`} onClick={() => setChanging(r.number)}>
                Change
              </button>
            </li>
          ))}
        </ul>
      )}
      {shared.length > 0 && (
        <div className="person-text-teams">
          <span className="field-label">Shared with their teams</span>
          <ul className="plain-list person-text-list">
            {shared.map((s) => (
              <li key={`${s.team.extension}-${s.number}`}>
                <Icon name={s.team.kind === 'callQueue' ? 'headset' : 'bell'} size={16} />
                <strong className="mono">{formatPhoneNumber(s.number)}</strong>
                <span className="muted">
                  {teamLabel(s.team.kind, s.team.name)} · {s.where}
                  {s.team.kind === 'callQueue' ? ', while signed in' : ''} · {s.team.handout === 'one' ? 'one at a time' : 'to everyone'}
                </span>
                <Link className="push" to={`${base}/${s.team.kind === 'callQueue' ? 'queues' : 'ring-groups'}?texting=${encodeURIComponent(s.team.extension)}`}>
                  Texting rules
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {saved && <Notice tone="ok">{saved}</Notice>}
    </div>
  );

  return (
    <>
      <Card
        title="Numbers for texts"
        action={
          page && inStitch && free.length > 0 ? (
            <Button size="small" variant="secondary" icon="plus" onClick={() => setGiving(true)}>
              Give a number
            </Button>
          ) : undefined
        }
      >
        {body}
      </Card>
      {giving && page && (
        <GiveNumberDialog
          domain={domain}
          extension={extension}
          name={name}
          page={page}
          numbers={free}
          onClose={() => setGiving(false)}
          onSaved={(message) => {
            setGiving(false);
            reload(message);
          }}
        />
      )}
      {row && page && (
        <TextsGoToDrawer
          domain={domain}
          row={row}
          page={page}
          onClose={() => setChanging(null)}
          onSaved={(_next, message) => {
            setChanging(null);
            reload(message);
          }}
        />
      )}
    </>
  );
}

/** Choose one of the company's numbers that can text: its texts go to this person (calls don't change). */
function GiveNumberDialog({
  domain,
  extension,
  name,
  page,
  numbers,
  onClose,
  onSaved
}: {
  domain: string;
  extension: string;
  name: string;
  page: TextRoutesPage;
  numbers: TextRouteRow[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const toAdmins = numbers.filter((r) => r.reach === 'admins' || r.reach === 'off');
  const elsewhere = numbers.filter((r) => r.reach === 'person' || r.reach === 'team');
  const [pick, setPick] = useState(toAdmins[0]?.number ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const chosen = numbers.find((r) => r.number === pick);
  const now = chosen ? textsGoTo(chosen, page.teams) : null;

  async function give() {
    if (!chosen) return;
    setBusy(true);
    setError('');
    try {
      await api.setTextRoute(domain, chosen.number, 'person', extension);
      onSaved(`Texts to ${chosen.label} go to ${name} now.`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const option = (r: TextRouteRow) => {
    const g = textsGoTo(r, page.teams);
    return (
      <label key={r.number} className="radio-card">
        <input type="radio" name="give-number" checked={pick === r.number} onChange={() => setPick(r.number)} />
        <span className="cell-stack">
          <strong className="mono">{r.label}</strong>
          <small>
            Texts go to {g.text === 'Admins' ? 'your admins' : g.text.replace(/^Same as calls · /, '')} now{g.note ? ` (${g.note.replace(/^Same as calls: /, '')})` : ''}
          </small>
        </span>
      </label>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`A number for ${name}’s texts`}
      subtitle="Its texts go to them. Calls to it don’t change."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!chosen || busy} onClick={() => void give()}>
            {busy ? 'Saving…' : `Give it to ${name}`}
          </Button>
        </>
      }
    >
      {numbers.length === 0 ? (
        <Empty title="No other numbers can text">Every number that can text already comes to them.</Empty>
      ) : (
        <fieldset className="plain-fieldset">
          <legend className="sr-only">Number</legend>
          {toAdmins.length > 0 && <span className="field-label">Going to your admins now</span>}
          {toAdmins.map(option)}
          {elsewhere.length > 0 && <span className="field-label">Going to someone else now</span>}
          {elsewhere.map(option)}
        </fieldset>
      )}
      {chosen && now && (chosen.reach === 'person' || chosen.reach === 'team') && (
        <Notice tone="warn">
          Texts to {chosen.label} go to {now.text.replace(/^Same as calls · /, '')} now. From now on they go to {name} instead, and so do its earlier conversations.
        </Notice>
      )}
      <p className="muted small tt-note">To have calls to it ring {name} too, use Give them a number under Phone numbers.</p>
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}
